// Slice-internal Konva renderer for rectangles (TR3).
//
// Extracted 1:1 from `objects/board-object-renderer.ts`
// (createRectangleNode / updateRectangleNode branch). The rectangle slice
// produces a `Konva.Rect` directly — NOT a `Konva.Group`. Other code paths
// that introspect rectangle nodes therefore use `instanceof Konva.Rect`.

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { RectangleObject } from './rectangle.interface.js';
import { konvaDashPattern, konvaDropShadowAttrs } from '../../objects/konva-decoration.helper.js';

/** Paint attributes shared by create/update — the rectangle's own look. */
function paintAttrs(obj: RectangleObject): Konva.RectConfig {
  return {
    fill: obj.fill || 'transparent',
    fillEnabled: obj.fill !== '',
    stroke: obj.strokeColor,
    strokeWidth: obj.strokeWidth,
    cornerRadius: obj.cornerRadius,
    // Optional effects (shared border-style / box-shadow / opacity widgets).
    opacity: obj.backgroundOpacity ?? 1,
    dash: konvaDashPattern(obj.strokeStyle, obj.strokeWidth),
    ...konvaDropShadowAttrs(obj.boxShadow === 'on'),
  };
}

/** Build a fresh Konva.Rect rendering the rectangle. */
export function createNode(obj: RectangleObject, _ctx: SliceRenderContext): Konva.Rect {
  return new Konva.Rect({
    id: obj.id,
    x: obj.x,
    y: obj.y,
    width: obj.width,
    height: obj.height,
    rotation: obj.rotation,
    ...paintAttrs(obj),
  });
}

/** Apply changed properties from an updated RectangleObject onto its node. */
export function updateNode(node: Konva.Node, obj: RectangleObject, _ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Rect)) return;
  // Base geometry (x/y/width/height/rotation) is applied centrally by
  // `applyBaseGeometry` in the element's onObjectUpdated; this method only
  // refreshes the rectangle's own paint attributes.
  node.setAttrs(paintAttrs(obj));
}
